import { LinkButton } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

type PaginationProps = {
  totalPages: number;
  totalResults: number;
  page?: number;
  currentlyShowing: { start: number; end: number };
  nextPage?: (e: MouseEvent<HTMLAnchorElement>) => void;
  prevPage?: (e: MouseEvent<HTMLAnchorElement>) => void;
};

const PageLink = ({
  page,
  className,
  text,
  onClick,
  icon,
}: {
  page: number;
  className?: string;
  text?: React.ReactNode;
  onClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  icon: React.ReactNode;
}) => {
  const location = useLocation();
  const { isMobileView } = useMobileView();
  const searchParams = new URLSearchParams(location.search);
  searchParams.set('page', page.toString());

  return !isMobileView ? (
    <Link to={`${location.pathname}?${searchParams.toString()}`} className={className} onClick={onClick}>
      {icon}
    </Link>
  ) : (
    <LinkButton
      to={`${location.pathname}?${searchParams.toString()}`}
      variant="primary"
      rightIcon={icon}
      onClick={onClick}>
      {text}
    </LinkButton>
  );
};

const Pagination = ({ totalPages, totalResults, page = 0, nextPage, prevPage, currentlyShowing }: PaginationProps) => {
  const { isMobileView } = useMobileView();

  return isMobileView ? (
    <div className="pagination px-md py-sm flex flex-row items-center justify-between">
      <div className="flex w-[120px] justify-start">
        {prevPage && <PageLink page={page - 1} icon={<Icon variant="chevron-left" />} onClick={prevPage} />}
      </div>
      <span className="flex-1 text-center font-semibold">{page + 1 + _t('of') + totalPages}</span>
      {nextPage && (
        <div className="flex w-[120px] justify-end">
          <PageLink
            icon={<Icon variant="chevron-right" />}
            page={page + 1}
            onClick={nextPage}
            text={_t('next')}></PageLink>
        </div>
      )}
    </div>
  ) : (
    <div className="flex items-center">
      <nav>
        <ul className="flex list-none items-center rounded">
          <li>
            {prevPage ? (
              <PageLink
                onClick={prevPage}
                className={`border-black-200 flex h-8 w-9 items-center justify-center rounded border bg-white shadow outline-none`}
                page={page - 1}
                icon={<Icon variant="chevron-left" />}></PageLink>
            ) : (
              <span
                className={`border-black-200 bg-black-200 flex h-8 w-9 items-center justify-center rounded border 
                outline-none
              `}>
                <Icon variant="chevron-left" color="fg_disabled" />
              </span>
            )}
          </li>
          <li>
            {nextPage ? (
              <PageLink
                className={`border-black-200 flex h-8 w-9 items-center justify-center rounded border bg-white shadow outline-none`}
                page={page + 1}
                onClick={nextPage}
                icon={<Icon variant="chevron-right" />}></PageLink>
            ) : (
              <span
                className={`border-black-200 bg-black-200 flex h-8 w-9 items-center justify-center rounded border outline-none`}>
                <Icon variant="chevron-right" color="fg_disabled" />
              </span>
            )}
          </li>
        </ul>
      </nav>
      <p className="text-black-600 ml-sm text-sm">
        {_t('paginationInfo1')} <span>{`${currentlyShowing.start} - ${currentlyShowing.end}`}</span>{' '}
        {_t('paginationInfo2')} <span>{totalResults}</span>
      </p>
    </div>
  );
};

export default Pagination;
